import { inject } from '@angular/core';
import { Auth, signOut, user } from '@fido/firebase/auth';
import { doc, Firestore, getDocFromServer } from '@fido/firebase/firestore';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

/**
 * Guard for organization-specific pages.
 * Ensures user is authenticated and has an organization membership.
 * - If not authenticated: redirect to sign-in
 * - If user has no organization: redirect to create-organization
 * - If user has organization: allow access
 */
export const organizationGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const firestore = inject(Firestore);
  const auth = inject(Auth);

  const u = await firstValueFrom(user(auth));

  if (!u) return router.createUrlTree(['/sign-in']);

  const appUser = await getDocFromServer(doc(firestore, 'users', u.uid));
  // beforeSignIn fails closed, so a missing doc here means the Auth account
  // exists without a Firestore projection — typically a legacy account that
  // predates the trigger, or a doc deleted out-of-band. beforeSignIn is the
  // only writer of this projection and only fires on sign-in, so we sign
  // the user out and bounce them to /sign-in; the next successful sign-in
  // re-fires the trigger and recreates the projection.
  if (!appUser.exists()) {
    // Let signOut rejection propagate. Swallowing it and returning the /sign-in
    // UrlTree would loop: /sign-in is guarded by redirectLoggedInTo(['/']), so a
    // still-authenticated user gets bounced to / → /home → this guard → repeat.
    // signOut is a local SDK op and rejection is exceedingly rare in practice.
    await signOut(auth);
    return router.createUrlTree(['/sign-in']);
  }
  if (!appUser.data()['organizationId']) return router.createUrlTree(['/onboarding']);
  return true;
};
